import { decimalToPercent } from "@citrine/utils/number";
import { isNumber } from "@citrine/utils/text";

import type { CandidateGroupFilter, FilterGroup, FilterGroups } from "./types";
import {
  isCategoricalDescriptorFilter,
  isFormulationFilter,
  isLabelFilter,
  isPerformanceFilter,
  isProcessFilter,
  isRealDescriptorFilter,
} from "./types";

const isUnsupportedFormulationFilter = (filter: CandidateGroupFilter) =>
  isLabelFilter(filter) &&
  filter.terms.some((subfilter) => subfilter.terms.length !== 1);

const isUnsupportedCategoricalFilter = (filter: CandidateGroupFilter) =>
  (isPerformanceFilter(filter) || isProcessFilter(filter)) &&
  filter.terms.some(
    (subfilter) => isCategoricalDescriptorFilter(subfilter) && subfilter.exclude
  );

/**
 * After the MVP, we decided to drop support for:
 * 1. Selecting multiple Ingredients/Labels in a Formulation subfilter
 * 2. Using the `exclude` option in Categorical subfilters
 */
const isUnsupportedFilterGroup = (filterGroup: FilterGroup) =>
  filterGroup.terms.some(
    (filter) =>
      isUnsupportedFormulationFilter(filter) ||
      isUnsupportedCategoricalFilter(filter)
  );

const isSupportedFilterGroup = (filterGroup: FilterGroup) =>
  !isUnsupportedFilterGroup(filterGroup);

export function transformFilterGroupsData(data: FilterGroups) {
  data.filter_groups.forEach((filterGroup) => {
    filterGroup.terms.forEach((filter) => {
      filter.terms.forEach((subFilter) => {
        if (
          isRealDescriptorFilter(subFilter) ||
          isFormulationFilter(subFilter)
        ) {
          // Turn any NaNs that are serialized as null back into NaN
          subFilter.min = subFilter.min ?? NaN;
          subFilter.max = subFilter.max ?? NaN;
        }

        if (
          isCategoricalDescriptorFilter(subFilter) &&
          subFilter.probability &&
          isNumber(subFilter.probability.min) &&
          !isNumber(subFilter.probability.min_percent)
        ) {
          // Migrate legacy categorical minimum probability filters that
          // have `min` set as a fraction (between 0 and 1) instead of
          // `minPercent` (between 0 and 100).
          subFilter.probability = {
            min_percent: decimalToPercent(subFilter.probability.min as number),
          };
        }
      });
    });
  });

  data.filter_groups = data.filter_groups.filter(isSupportedFilterGroup);
  return data;
}
